import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Plus, Edit, Eye, Save, X, Filter, Download, AlertCircle, Upload, CheckCircle, FileText } from 'lucide-react';
import Layout from '../component/Layout';
import { db } from '../services/firebase';
import { collection, getDocs, addDoc, updateDoc, doc } from 'firebase/firestore';
import './ProductConstraints.css';

// ✅ Import master data from JSON
import productMasterData from './ProductMasterData.json';

const ProductConstraints = () => {
  const [editingProduct, setEditingProduct] = useState(null);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);

  // ── Upload state ──
  const uploadInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadReport, setUploadReport] = useState(null); // { added, duplicates, invalid, errors }
  const [showUploadModal, setShowUploadModal] = useState(false);

  // ✅ Extract data from the JSON structure
  const extractMasterData = (jsonData) => {
    const segments = Object.keys(jsonData); // GOLD, SILVER, PLATINUM, DIAMOND
    const categoriesBySegment = {};
    const productSources = new Set();
    const productNamesBySource = {};
    const finishTypes = new Set();
    const specifications = new Set();

    // Process each material type (segment)
    Object.entries(jsonData).forEach(([segment, purities]) => {
      if (purities && typeof purities === 'object') {
        categoriesBySegment[segment] = Object.keys(purities);

        // Process each purity level
        Object.values(purities).forEach(sources => {
          if (sources && typeof sources === 'object') {
            Object.entries(sources).forEach(([source, products]) => {
              productSources.add(source);

              if (!productNamesBySource[source]) {
                productNamesBySource[source] = [];
              }

              // Process each product
              if (products && typeof products === 'object') {
                Object.entries(products).forEach(([productName, productData]) => {
                  if (productName && !productNamesBySource[source].includes(productName)) {
                    productNamesBySource[source].push(productName);
                  }

                  // Process finish types
                  if (productData && productData.finishTypes && typeof productData.finishTypes === 'object') {
                    Object.keys(productData.finishTypes).forEach(finishType => {
                      finishTypes.add(finishType);
                    });

                    // Process specifications from finish types
                    Object.values(productData.finishTypes).forEach(specs => {
                      if (Array.isArray(specs)) {
                        specs.forEach(spec => {
                          specifications.add(spec);
                        });
                      }
                    });
                  }
                });
              }
            });
          }
        });
      }
    });

    return {
      segments,
      categoriesBySegment,
      productSources: Array.from(productSources),
      productNamesBySource,
      finishTypes: Array.from(finishTypes),
      specifications: Array.from(specifications)
    };
  };

  const {
    segments,
    categoriesBySegment
  } = extractMasterData(productMasterData);

  // ── Count every valid leaf combination in master data (segment×category×source×name×finish×spec) ──
  const totalMasterCombinations = useMemo(() => {
    let count = 0;
    Object.entries(productMasterData).forEach(([, categories]) => {
      Object.entries(categories || {}).forEach(([, sources]) => {
        Object.entries(sources || {}).forEach(([, products]) => {
          Object.entries(products || {}).forEach(([, productData]) => {
            Object.entries(productData?.finishTypes || {}).forEach(([, specs]) => {
              if (Array.isArray(specs)) count += specs.length;
            });
          });
        });
      });
    });
    return count;
  }, []);

  const [productSpecs, setProductSpecs] = useState([]);

  const newProductTemplate = {
    segment: '',
    category: '',
    buyerDeskCalculation: '',
    productSource: '',
    productName: '',
    finishType: '',
    specification: '',
    maxWastageSeller: '',
    wastageOperator: '',
    wastageJmi: '',
    maxSetMakingSeller: '',
    setMakingOperator: '',
    setMakingJmi: '',
    maxGramMakingSeller: '',
    gramMakingOperator: '',
    gramMakingJmi: '',
    maxSpecificationMakingSeller: 0,
    specificationMakingOperator: 0,
    specificationMakingJmi: 0,
    maxSpecificationGramRateSeller: 0,
    specificationGramRateOperator: 0,
    specificationGramRateJmi: 0,
    maxReadyServeSla: '',
    orderServeSla: '',
  };

const [newProduct, setNewProduct] = useState(newProductTemplate);

   const [filters, setFilters] = useState({
     segment: '',
     category: '',
     productSource: '',
     productName: '',
     finishType: '',
     specification: '',
   });

   // Pagination state
   const [currentPage, setCurrentPage] = useState(1);
   const [itemsPerPage, setItemsPerPage] = useState(10); // Default 10 items per page

  // ✅ Check if combination is valid in master data
  const isValidCombination = (product) => {
    const { segment, category, productSource, productName, finishType, specification } = product;

    // Check if the product exists in master data
    const productData = productMasterData[segment]?.[category]?.[productSource]?.[productName];
    if (!productData) return false;

    // Check if finish type exists for this product
    if (!productData.finishTypes || !productData.finishTypes[finishType]) return false;

    // Check if specification is allowed for this finish type
    const allowedSpecs = productData.finishTypes[finishType];
    return Array.isArray(allowedSpecs) && allowedSpecs.includes(specification);
  };

  // Fetch from Firestore
  useEffect(() => {
    const fetchProductSpecs = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'productSpecs'));
        const specsData = [];
        // Normalize the 6 identity fields to strings so number-typed Firestore
        // values (e.g. category: 840) match string values used by filters/selects.
        const toStr = (v) => (v === null || v === undefined) ? '' : String(v);
        querySnapshot.forEach((doc) => {
          const d = doc.data();
          const seg = toStr(d.segment).trim().toUpperCase();
          // Pearl & Gems are managed exclusively under Pearl & Gems Constraints
          if (seg === "PEARL" || seg === "GEMS") return;

          specsData.push({
            id: doc.id,
            ...d,
            segment:       toStr(d.segment),
            category:      toStr(d.category),
            productSource: toStr(d.productSource),
            productName:   toStr(d.productName),
            finishType:    toStr(d.finishType),
            specification: toStr(d.specification),
          });
        });
        setProductSpecs(specsData);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching product specs:', error);
        setLoading(false);
      }
    };

    fetchProductSpecs();
  }, []);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    if (name === 'segment') {
      setFilters(prev => ({ ...prev, segment: value, category: '', productSource: '', productName: '', finishType: '', specification: '' }));
    } else if (name === 'category') {
      setFilters(prev => ({ ...prev, category: value, productSource: '', productName: '', finishType: '', specification: '' }));
    } else if (name === 'productSource') {
      setFilters(prev => ({ ...prev, productSource: value, productName: '', finishType: '', specification: '' }));
    } else if (name === 'finishType') {
      setFilters(prev => ({ ...prev, finishType: value, specification: '' }));
    } else {
      setFilters(prev => ({ ...prev, [name]: value }));
    }
  };

// Filter specs based on filters
   const filteredSpecs = useMemo(() => {
     return productSpecs.filter(product => {
       // Use String() so number-typed Firestore fields match string filter values
       const toS = (v) => String(v ?? '');
       const segmentMatch       = !filters.segment       || toS(product.segment)       === filters.segment;
       const categoryMatch      = !filters.category      || toS(product.category)      === filters.category;
       const productSourceMatch = !filters.productSource || toS(product.productSource) === filters.productSource;
       const productNameMatch   = !filters.productName   || toS(product.productName)   === filters.productName;
       const finishTypeMatch    = !filters.finishType    || toS(product.finishType)    === filters.finishType;
       const specificationMatch = !filters.specification || toS(product.specification) === filters.specification;

       return segmentMatch && categoryMatch && productSourceMatch && productNameMatch && finishTypeMatch && specificationMatch;
     });
   }, [productSpecs, filters]);

   // Paginate filtered specs
   const paginatedSpecs = useMemo(() => {
     const startIndex = (currentPage - 1) * itemsPerPage;
     const endIndex = startIndex + itemsPerPage;
     return filteredSpecs.slice(startIndex, endIndex);
   }, [filteredSpecs, currentPage, itemsPerPage]);

   // Calculate total pages
   const totalPages = useMemo(() => {
     return Math.max(1, Math.ceil(filteredSpecs.length / itemsPerPage));
   }, [filteredSpecs.length, itemsPerPage]);

  // ── KPI computations ──
  const kpiData = useMemo(() => {
    const covered = productSpecs.length;
    const uniqueSegments = new Set(productSpecs.map(p => p.segment).filter(Boolean)).size;
    const uniqueSources = new Set(productSpecs.map(p => p.productSource).filter(Boolean)).size;
    const parseNum = (v) => parseFloat(v) || 0;
    let totalWastage = 0, totalMC = 0, totalSLA = 0, slaCount = 0;
    productSpecs.forEach(p => {
      totalWastage += parseNum(p.maxWastageSeller) + parseNum(p.wastageOperator) + parseNum(p.wastageJmi);
      totalMC += parseNum(p.maxSetMakingSeller) + parseNum(p.setMakingOperator) + parseNum(p.setMakingJmi)
               + parseNum(p.maxGramMakingSeller) + parseNum(p.gramMakingOperator) + parseNum(p.gramMakingJmi);
      const sla = parseNum(p.orderServeSla);
      if (sla > 0) { totalSLA += sla; slaCount++; }
    });
    const pct = totalMasterCombinations > 0
      ? Math.round((covered / totalMasterCombinations) * 100)
      : 0;
    return {
      covered,
      totalMaster: totalMasterCombinations,
      pct,
      uniqueSegments,
      uniqueSources,
      avgWastage: covered > 0 ? (totalWastage / covered).toFixed(2) : '0.00',
      avgMC: covered > 0 ? (totalMC / covered).toFixed(2) : '0.00',
      avgSLA: slaCount > 0 ? (totalSLA / slaCount).toFixed(1) : '—',
    };
  }, [productSpecs, totalMasterCombinations]);

  const clearFilters = () => {
    setFilters({
      segment: '',
      category: '',
      productSource: '',
      productName: '',
      finishType: '',
      specification: '',
    });
  };

  // Check for duplicate (same 6-key combo) — compare as strings to handle mixed types
  const isDuplicateEntry = (product) => {
    const toS = (v) => String(v ?? '');
    return productSpecs.some(spec =>
      toS(spec.segment)       === toS(product.segment)       &&
      toS(spec.category)      === toS(product.category)      &&
      toS(spec.productSource) === toS(product.productSource) &&
      toS(spec.productName)   === toS(product.productName)   &&
      toS(spec.finishType)    === toS(product.finishType)    &&
      toS(spec.specification) === toS(product.specification)
    );
  };

  const handleEdit = (product) => {
    // Ensure all numeric fields are properly set, including operator rates
    const editProduct = {
      ...product,
      specificationGramRateOperator: product.specificationGramRateOperator ?? 0,
      // Ensure other rate fields are also properly initialized
      wastageOperator: product.wastageOperator ?? '',
      setMakingOperator: product.setMakingOperator ?? '',
      gramMakingOperator: product.gramMakingOperator ?? '',
      specificationMakingOperator: product.specificationMakingOperator ?? 0,
    };
    console.log('Editing product with operator rate:', editProduct.specificationGramRateOperator);
    setEditingProduct(editProduct);
    setError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      if (!isValidCombination(editingProduct)) {
        setError('Selected product combination is not allowed in master data.');
        setAdding(false);
        return;
      }

      const docRef = doc(db, 'productSpecs', editingProduct.id);
      await updateDoc(docRef, editingProduct);
      setProductSpecs(prev =>
        prev.map(p => p.id === editingProduct.id ? editingProduct : p)
      );
      setEditingProduct(null);
      setError('');
    } catch (error) {
      console.error('Error updating product:', error);
      setError('Failed to update product. Please try again.');
    }
    setAdding(false);
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();

    if (!isValidCombination(newProduct)) {
      setError('Selected product combination is not allowed in master data.');
      return;
    }

    if (isDuplicateEntry(newProduct)) {
      setError('A product with these specifications already exists!');
      return;
    }

    try {
      const docRef = await addDoc(collection(db, 'productSpecs'), newProduct);
      setProductSpecs(prev => [...prev, { ...newProduct, id: docRef.id }]);
      setShowAddProduct(false);
      setError('');
    } catch (error) {
      console.error('Error adding product:', error);
      setError('Failed to add product. Please try again.');
    }
  };

  // ── Helpers to handle both string and number constraint values ──
  // Returns true when value is a real finite number (not empty/null/undefined/NaN)
  const isNumericVal = (v) => {
    if (v === '' || v === null || v === undefined) return false;
    return isFinite(parseFloat(v));
  };

  // Format a single raw value for display:
  //   empty / null / undefined  →  '—'
  //   non-numeric string (e.g. "N/A", "text")  →  show as-is
  //   numeric  →  toFixed(1)
  const fmtVal = (v) => {
    if (v === '' || v === null || v === undefined) return '—';
    const n = parseFloat(v);
    if (isNaN(n)) return String(v);   // non-numeric: show raw string
    return n.toFixed(1);
  };

  // Safe numeric parse: returns 0 for non-numeric so totals stay numeric
  const safeNum = (v) => { const n = parseFloat(v); return isFinite(n) ? n : 0; };

  // Build a {seller, operator, jmi, total} group.
  // Total is computed only when all three inputs are numeric; otherwise '—'.
  const makeGroup = (s, o, j) => {
    const allNum = isNumericVal(s) && isNumericVal(o) && isNumericVal(j);
    return {
      seller:   fmtVal(s),
      operator: fmtVal(o),
      jmi:      fmtVal(j),
      total:    allNum ? (safeNum(s) + safeNum(o) + safeNum(j)).toFixed(1) : '—',
    };
  };

  // Calculate totals — works for both number and string field values
  const calculateTotals = (product) => ({
    wastage:               makeGroup(product.maxWastageSeller,             product.wastageOperator,             product.wastageJmi),
    setMaking:             makeGroup(product.maxSetMakingSeller,           product.setMakingOperator,           product.setMakingJmi),
    gramMaking:            makeGroup(product.maxGramMakingSeller,          product.gramMakingOperator,          product.gramMakingJmi),
    specificationMaking:   makeGroup(product.maxSpecificationMakingSeller, product.specificationMakingOperator, product.specificationMakingJmi),
    specificationGramRate: makeGroup(product.maxSpecificationGramRateSeller, product.specificationGramRateOperator, product.specificationGramRateJmi),
  });

  const downloadExcel = () => {
    let csvContent = "Segment,Category,Product Source,Product Name,Finish Type,Specification,";
    csvContent += "Wastage Seller,Wastage Operator,Wastage JMI,Total Wastage,";
    csvContent += "Set MC Seller,Set MC Operator,Set MC JMI,Total Set MC,";
    csvContent += "Gram MC Seller,Gram MC Operator,Gram MC JMI,Total Gram MC,";
    csvContent += "Spec Wastage Seller,Spec Wastage Operator,Spec Wastage JMI,Total Spec Wastage,";
    csvContent += "Spec Gram Rate Seller,Spec Gram Rate Operator,Spec Gram Rate JMI,Total Spec Gram Rate,";
    csvContent += "Ready Serve SLA,Order Serve SLA\n";

    filteredSpecs.forEach(product => {
      const totals = calculateTotals(product);
      csvContent += `${product.segment || ''},${product.category || ''},${product.productSource || ''},`;
      csvContent += `${product.productName || ''},${product.finishType || ''},${product.specification || ''},`;
      csvContent += `${totals.wastage.seller},${totals.wastage.operator},${totals.wastage.jmi},${totals.wastage.total},`;
      csvContent += `${totals.setMaking.seller},${totals.setMaking.operator},${totals.setMaking.jmi},${totals.setMaking.total},`;
      csvContent += `${totals.gramMaking.seller},${totals.gramMaking.operator},${totals.gramMaking.jmi},${totals.gramMaking.total},`;
      csvContent += `${totals.specificationMaking.seller},${totals.specificationMaking.operator},${totals.specificationMaking.jmi},${totals.specificationMaking.total},`;
      csvContent += `${totals.specificationGramRate.seller},${totals.specificationGramRate.operator},${totals.specificationGramRate.jmi},${totals.specificationGramRate.total},`;
      csvContent += `${product.maxReadyServeSla || ''},${product.orderServeSla || ''}\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'product_constraints.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // \u2500\u2500 Download blank template (xlsx) \u2500\u2500
  const downloadTemplate = async () => {
    const XLSX = await import('xlsx');
    const headers = [
      'segment','category','buyerDeskCalculation','productSource','productName','finishType','specification',
      'maxWastageSeller','wastageOperator','wastageJmi',
      'maxSetMakingSeller','setMakingOperator','setMakingJmi',
      'maxGramMakingSeller','gramMakingOperator','gramMakingJmi',
      'maxSpecificationMakingSeller','specificationMakingOperator','specificationMakingJmi',
      'maxSpecificationGramRateSeller','specificationGramRateOperator','specificationGramRateJmi',
      'maxReadyServeSla','orderServeSla',
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers]);
    // Column widths
    ws['!cols'] = headers.map(h => ({ wch: Math.max(h.length + 4, 18) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'ProductConstraints');
    XLSX.writeFile(wb, 'product_constraints_template.xlsx');
  };

  // \u2500\u2500 Excel upload handler \u2500\u2500
  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // reset input so same file can be re-selected after a fix
    e.target.value = '';

    const XLSX = await import('xlsx');
    setUploading(true);

    const REQUIRED_KEYS = [
      'segment','category','buyerDeskCalculation','productSource','productName',
      'finishType','specification',
      'maxWastageSeller','wastageOperator','wastageJmi',
      'maxSetMakingSeller','setMakingOperator','setMakingJmi',
      'maxGramMakingSeller','gramMakingOperator','gramMakingJmi',
      'maxSpecificationMakingSeller','specificationMakingOperator','specificationMakingJmi',
      'maxSpecificationGramRateSeller','specificationGramRateOperator','specificationGramRateJmi',
      'maxReadyServeSla','orderServeSla',
    ];

    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });

      if (!rows.length) {
        setUploadReport({ added: 0, duplicates: 0, invalid: [{ row: '—', reason: 'File is empty or has no data rows.' }], errors: [] });
        setShowUploadModal(true);
        setUploading(false);
        return;
      }

      // Build a Set of existing combo keys for O(1) duplicate lookup
      const existingKeys = new Set(
        productSpecs.map(p =>
          `${p.segment}||${p.category}||${p.productSource}||${p.productName}||${p.finishType}||${p.specification}`.toUpperCase()
        )
      );
      // Also track keys added in this upload batch to catch in-file duplicates
      const batchKeys = new Set();

      const report = { added: 0, duplicates: 0, invalid: [], errors: [] };
      const newDocs = []; // { data } to add

      rows.forEach((raw, idx) => {
        const rowNum = idx + 2; // 1-indexed, row 1 = header

        // 1. Trim all string values
        const row = {};
        REQUIRED_KEYS.forEach(k => {
          row[k] = typeof raw[k] === 'string' ? raw[k].trim() : (raw[k] ?? '');
        });

        // 2. Check required identity fields are present
        const IDENTITY = ['segment','category','productSource','productName','finishType','specification'];
        const missingId = IDENTITY.filter(k => !row[k]);
        if (missingId.length) {
          report.invalid.push({ row: rowNum, reason: `Missing required field(s): ${missingId.join(', ')}` });
          return;
        }

        // 3. Validate against master data
        const masterValid = (() => {
          const pd = productMasterData[row.segment]?.[row.category]?.[row.productSource]?.[row.productName];
          if (!pd) return false;
          if (!pd.finishTypes?.[row.finishType]) return false;
          const specs = pd.finishTypes[row.finishType];
          return Array.isArray(specs) && specs.includes(row.specification);
        })();

        if (!masterValid) {
          report.invalid.push({ row: rowNum, reason: `Combination not found in master data (${row.segment} › ${row.category} › ${row.productSource} › ${row.productName} › ${row.finishType} › ${row.specification})` });
          return;
        }

        // 4. Duplicate check — existing DB + in-file
        const comboKey = `${row.segment}||${row.category}||${row.productSource}||${row.productName}||${row.finishType}||${row.specification}`.toUpperCase();
        if (existingKeys.has(comboKey) || batchKeys.has(comboKey)) {
          report.duplicates++;
          return;
        }
        batchKeys.add(comboKey);

        // 5. Coerce numeric fields
        const numField = (v) => parseFloat(v) || 0;
        newDocs.push({
          segment: row.segment,
          category: row.category,
          buyerDeskCalculation: row.buyerDeskCalculation,
          productSource: row.productSource,
          productName: row.productName,
          finishType: row.finishType,
          specification: row.specification,
          maxWastageSeller: numField(row.maxWastageSeller),
          wastageOperator: numField(row.wastageOperator),
          wastageJmi: numField(row.wastageJmi),
          maxSetMakingSeller: numField(row.maxSetMakingSeller),
          setMakingOperator: numField(row.setMakingOperator),
          setMakingJmi: numField(row.setMakingJmi),
          maxGramMakingSeller: numField(row.maxGramMakingSeller),
          gramMakingOperator: numField(row.gramMakingOperator),
          gramMakingJmi: numField(row.gramMakingJmi),
          maxSpecificationMakingSeller: numField(row.maxSpecificationMakingSeller),
          specificationMakingOperator: numField(row.specificationMakingOperator),
          specificationMakingJmi: numField(row.specificationMakingJmi),
          maxSpecificationGramRateSeller: numField(row.maxSpecificationGramRateSeller),
          specificationGramRateOperator: numField(row.specificationGramRateOperator),
          specificationGramRateJmi: numField(row.specificationGramRateJmi),
          maxReadyServeSla: numField(row.maxReadyServeSla),
          orderServeSla: numField(row.orderServeSla),
        });
      });

      // 6. Write valid new docs to Firestore
      const addedDocs = [];
      for (const data of newDocs) {
        try {
          const ref = await addDoc(collection(db, 'productSpecs'), data);
          addedDocs.push({ ...data, id: ref.id });
          report.added++;
        } catch (err) {
          report.errors.push(err.message);
        }
      }

      // 7. Update local state
      if (addedDocs.length) {
        setProductSpecs(prev => [...prev, ...addedDocs]);
      }

      setUploadReport(report);
      setShowUploadModal(true);
    } catch (err) {
      setUploadReport({ added: 0, duplicates: 0, invalid: [], errors: [`Failed to parse file: ${err.message}`] });
      setShowUploadModal(true);
    }
    setUploading(false);
  };

  // ✅ Updated renderProductForm to use master data validation
  const renderProductForm = (product, setProduct, onSubmit, isEditing = false) => {


    return (
      <form onSubmit={onSubmit} className="product-form">
        {error && (
          <div className="error-message">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        <div className="form-section">
          <h4>Segment Details</h4>
          <div className="form-grid">
            <div className="form-field">
              <label>Segment</label>
              <select
                value={product.segment}
                onChange={(e) => setProduct(prev => ({ ...prev, segment: e.target.value, category: '' }))}
                required
              >
                <option value="">Select Segment</option>
                {segments.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label>Category (Purity)</label>
              <select
                value={product.category}
                onChange={(e) => setProduct(prev => ({ ...prev, category: e.target.value }))}
                required
                disabled={!product.segment}
              >
                <option value="">Select Category</option>
                {product.segment && categoriesBySegment[product.segment]?.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Purity Calculation on Buyers Desk</label>
              <input
                type="text"
                value={product.buyerDeskCalculation}
                onChange={(e) => setProduct(prev => ({ ...prev, buyerDeskCalculation: e.target.value }))}
                required
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h4>Product Details</h4>
          <div className="form-grid">
            <div className="form-field">
              <div className="form-field">
                <label>Product Source</label>
                <select
                  value={product.productSource}
                  onChange={(e) =>
                    setProduct((prev) => ({
                      ...prev,
                      productSource: e.target.value,
                      productName: '',
                      finishType: '',
                      specification: '',
                    }))
                  }
                  required
                  disabled={!product.category}
                >
                  <option value="">Select Source</option>
                  {product.category &&
                    Object.keys(productMasterData[product.segment]?.[product.category] || {}).map(
                      (source) => (
                        <option key={source} value={source}>
                          {source}
                        </option>
                      )
                    )}
                </select>
              </div>
            </div>
            <div className="form-field">
              <label>Product Name</label>
              <select
                value={product.productName}
                onChange={(e) =>
                  setProduct((prev) => ({
                    ...prev,
                    productName: e.target.value,
                    finishType: '',
                    specification: '',
                  }))
                }
                required
                disabled={!product.productSource}
              >
                <option value="">Select Product Name</option>
                {product.productSource &&
                  Object.keys(
                    productMasterData[product.segment]?.[product.category]?.[product.productSource] ||
                    {}
                  ).map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="form-field">
              <label>Finish Type</label>
              <select
                value={product.finishType}
                onChange={(e) =>
                  setProduct((prev) => ({
                    ...prev,
                    finishType: e.target.value,
                    specification: '',
                  }))
                }
                required
                disabled={!product.productName}
              >
                <option value="">Select Finish Type</option>
                {product.productName &&
                  Object.keys(
                    productMasterData[product.segment]?.[product.category]?.[product.productSource]?.[
                      product.productName
                    ]?.finishTypes || {}
                  ).map((finish) => (
                    <option key={finish} value={finish}>
                      {finish}
                    </option>
                  ))}
              </select>
            </div>

            <div className="form-field">
              <label>Specification</label>
              <select
                value={product.specification}
                onChange={(e) =>
                  setProduct((prev) => ({ ...prev, specification: e.target.value }))
                }
                required
                disabled={!product.finishType}
              >
                <option value="">Select Specification</option>
                {product.finishType &&
                  (() => {
                    const specs = productMasterData[product.segment]?.[product.category]?.[product.productSource]?.[
                      product.productName
                    ]?.finishTypes?.[product.finishType];
                    return Array.isArray(specs) ? specs.map((spec) => (
                      <option key={spec} value={spec}>
                        {spec}
                      </option>
                    )) : null;
                  })()}
              </select>
            </div>
          </div>
        </div>

        {/* Wastage Section */}
        <div className="form-section">
          <h4>Wastage Percentage (%)</h4>
          <div className="form-grid">
            <div className="form-field">
              <label>Max Seller Wastage</label>
              <input
                type="number"
                step="0.1"
                value={product.maxWastageSeller}
                onChange={(e) => setProduct(prev => ({ ...prev, maxWastageSeller: e.target.value }))}
                required
              />
            </div>
            <div className="form-field">
              <label>Operator Wastage in percentage(%)</label>
              <input
                type="number"
                step="0.1"
                value={product.wastageOperator}
                onChange={(e) => setProduct(prev => ({ ...prev, wastageOperator: e.target.value }))}
                required
              />
            </div>
            <div className="form-field">
              <label>JMI Wastage in percentage(%)</label>
              <input
                type="number"
                step="0.1"
                value={product.wastageJmi}
                onChange={(e) => setProduct(prev => ({ ...prev, wastageJmi: e.target.value }))}
                required
              />
            </div>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Max Seller Specification Wastage</label>
              <input
                type="number"
                step="0.1"
                value={product.maxSpecificationMakingSeller}
                onChange={(e) => setProduct(prev => ({ ...prev, maxSpecificationMakingSeller: e.target.value }))}
                required
              />
            </div>
            <div className="form-field">
              <label>Operator Specification Wastage in percentage(%)</label>
              <input
                type="number"
                step="0.1"
                value={product.specificationMakingOperator}
                onChange={(e) => setProduct(prev => ({ ...prev, specificationMakingOperator: e.target.value }))}
                required
              />
            </div>
            <div className="form-field">
              <label>JMI Specification Wastage in percentage(%)</label>
              <input
                type="number"
                step="0.1"
                value={product.specificationMakingJmi}
                onChange={(e) => setProduct(prev => ({ ...prev, specificationMakingJmi: e.target.value }))}
                required
              />
            </div>
          </div>
        </div>

        {/* Making Charges Section */}
        <div className="form-section">
          <h4>Making Charges Percentage </h4>
          <div className="form-grid">
            <div className="form-field">
              <label>Max Seller Set MC</label>
              <input
                type="number"
                step="0.1"
                value={product.maxSetMakingSeller}
                onChange={(e) => setProduct(prev => ({ ...prev, maxSetMakingSeller: e.target.value }))}
                required
              />
            </div>
            <div className="form-field">
              <label>Operator Set MC in percentage(%)</label>
              <input
                type="number"
                step="0.1"
                value={product.setMakingOperator}
                onChange={(e) => setProduct(prev => ({ ...prev, setMakingOperator: e.target.value }))}
                required
              />
            </div>
            <div className="form-field">
              <label>JMI Set MC in percentage(%)</label>
              <input
                type="number"
                step="0.1"
                value={product.setMakingJmi}
                onChange={(e) => setProduct(prev => ({ ...prev, setMakingJmi: e.target.value }))}
                required
              />
            </div>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Max Seller Net Gram MC</label>
              <input
                type="number"
                step="0.1"
                value={product.maxGramMakingSeller}
                onChange={(e) => setProduct(prev => ({ ...prev, maxGramMakingSeller: e.target.value }))}
                required
              />
            </div>
            <div className="form-field">
              <label>Operator  Net Gram MC in percentage(%)</label>
              <input
                type="number"
                step="0.1"
                value={product.gramMakingOperator}
                onChange={(e) => setProduct(prev => ({ ...prev, gramMakingOperator: e.target.value }))}
                required
              />
            </div>
            <div className="form-field">
              <label>JMI Net Gram MC in percentage(%)</label>
              <input
                type="number"
                step="0.1"
                value={product.gramMakingJmi}
                onChange={(e) => setProduct(prev => ({ ...prev, gramMakingJmi: e.target.value }))}
                required
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h4>Specification Gram Rate</h4>
          <div className="form-grid">
            <div className="form-field">
              <label>Max Seller Spec Gram Rate</label>
              <input
                type="number"
                step="0.1"
                value={product.maxSpecificationGramRateSeller}
                onChange={(e) => setProduct(prev => ({ ...prev, maxSpecificationGramRateSeller: e.target.value }))}
                required
              />
            </div>
            <div className="form-field">
              <label>Operator Spec Gram Rate in percentage(%)</label>
              <input
                type="number"
                step="0.1"
                value={product.specificationGramRateOperator}
                onChange={(e) => setProduct(prev => ({ ...prev, specificationGramRateOperator: e.target.value }))}
                required
              />
            </div>
            <div className="form-field">
              <label>JMI Spec Gram Rate in percentage(%)</label>
              <input
                type="number"
                step="0.1"
                value={product.specificationGramRateJmi}
                onChange={(e) => setProduct(prev => ({ ...prev, specificationGramRateJmi: e.target.value }))}
                required
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h4>SLA Details</h4>
          <div className="form-grid">
            <div className="form-field">
              <label>Max Ready Serve SLA (Days)</label>
              <input
                type="number"
                value={product.maxReadyServeSla}
                onChange={(e) => setProduct(prev => ({ ...prev, maxReadyServeSla: e.target.value }))}
                required
              />
            </div>
            <div className="form-field">
              <label>Order Serve SLA (Days)</label>
              <input
                type="number"
                value={product.orderServeSla}
                onChange={(e) => setProduct(prev => ({ ...prev, orderServeSla: e.target.value }))}
                required
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => {
              if (onSubmit === handleAddProduct) {
                setShowAddProduct(false);
                setNewProduct(newProductTemplate);
              }
              if (onSubmit === handleSave) setEditingProduct(null);
              setError('');
            }}
            className="btn-secondary"
          >
            Cancel
          </button>
          {!adding && (
            <button type="submit" className="btn-primary">
              {isEditing ? 'Save Changes' : 'Add Product'}
            </button>
          )}
        </div>
      </form>
    );
  };

  if (loading) {
    return (
      <Layout pageTitle="Product Constraints">
        <div className="loading">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout pageTitle="Product Constraints">
      <div className="product-constraints-container">
        <h1>Product Constraints</h1>
        <p>Set constraints for product orders, including SLA, gram making, and more.</p>

        {/* ── KPI Cards ── */}
        <div className="pc-kpi-grid">
          {/* ── Coverage KPI (wide card) ── */}
          <div className="pc-kpi-card pc-kpi-coverage" style={{gridColumn: '1 / -1'}}>
            <div className="pc-kpi-icon" style={{fontSize:'2rem'}}>📊</div>
            <div className="pc-kpi-body" style={{flex:1}}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline', flexWrap:'wrap', gap:'0.5rem'}}>
                <div className="pc-kpi-label" style={{textTransform:'none', fontSize:'0.9rem', fontWeight:700, letterSpacing:0}}>Coverage Progress</div>
                <div style={{display:'flex', gap:'1.2rem', alignItems:'baseline'}}>
                  <span style={{fontWeight:700, fontSize:'1.1rem', color:'#8A6B32'}}>
                    {loading ? '—' : kpiData.covered.toLocaleString()} Covered
                  </span>
                  <span style={{color:'#9ca3af', fontSize:'0.85rem'}}>/</span>
                  <span style={{fontWeight:600, fontSize:'1rem', color:'#4a5568'}}>
                    {kpiData.totalMaster.toLocaleString()} Total
                  </span>
                  <span className="pc-kpi-pct-badge">{kpiData.pct}%</span>
                </div>
              </div>
              <div className="pc-kpi-progress-track">
                <div className="pc-kpi-progress-fill" style={{width: `${kpiData.pct}%`}} />
              </div>
              <div className="pc-kpi-hint">
                {loading ? 'Loading…' : `${(kpiData.totalMaster - kpiData.covered).toLocaleString()} combinations still unconfigured`}
              </div>
            </div>
          </div>

          {/* ── Stat KPI cards ── */}
          <div className="pc-kpi-card pc-kpi-total">
            <div className="pc-kpi-icon">📦</div>
            <div className="pc-kpi-body">
              <div className="pc-kpi-value">{loading ? '—' : kpiData.covered.toLocaleString()}</div>
              <div className="pc-kpi-label">Configured</div>
              <div className="pc-kpi-hint">Saved product specs</div>
            </div>
          </div>

          <div className="pc-kpi-card pc-kpi-segments">
            <div className="pc-kpi-icon">🏷️</div>
            <div className="pc-kpi-body">
              <div className="pc-kpi-value">{loading ? '—' : kpiData.uniqueSegments}</div>
              <div className="pc-kpi-label">Segments</div>
              <div className="pc-kpi-hint">e.g. Gold, Silver…</div>
            </div>
          </div>

          <div className="pc-kpi-card pc-kpi-sources">
            <div className="pc-kpi-icon">🔗</div>
            <div className="pc-kpi-body">
              <div className="pc-kpi-value">{loading ? '—' : kpiData.uniqueSources}</div>
              <div className="pc-kpi-label">Product Sources</div>
              <div className="pc-kpi-hint">Unique supply origins</div>
            </div>
          </div>

          <div className="pc-kpi-card pc-kpi-wastage">
            <div className="pc-kpi-icon">♻️</div>
            <div className="pc-kpi-body">
              <div className="pc-kpi-value">{loading ? '—' : `${kpiData.avgWastage}%`}</div>
              <div className="pc-kpi-label">Avg Wastage</div>
              <div className="pc-kpi-hint">S + O + JMI average</div>
            </div>
          </div>

          <div className="pc-kpi-card pc-kpi-mc">
            <div className="pc-kpi-icon">💰</div>
            <div className="pc-kpi-body">
              <div className="pc-kpi-value">{loading ? '—' : `${kpiData.avgMC}%`}</div>
              <div className="pc-kpi-label">Avg MC %</div>
              <div className="pc-kpi-hint">Set + Gram MC avg</div>
            </div>
          </div>

          <div className="pc-kpi-card pc-kpi-sla">
            <div className="pc-kpi-icon">⏱️</div>
            <div className="pc-kpi-body">
              <div className="pc-kpi-value">{loading ? '—' : kpiData.avgSLA === '—' ? '—' : `${kpiData.avgSLA}d`}</div>
              <div className="pc-kpi-label">Avg Order SLA</div>
              <div className="pc-kpi-hint">Average serve days</div>
            </div>
          </div>
        </div>

        <div className="content-wrapper">
          {/* Hidden file input for Excel upload */}
          <input
            ref={uploadInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
            onChange={handleExcelUpload}
          />

          <div className="header-section">
            <h2>Product Specifications</h2>
            <div className="header-actions">
              <button onClick={downloadTemplate} className="btn-secondary download-btn" title="Download blank template">
                <FileText size={16} /> Template
              </button>
              <button onClick={downloadExcel} className="btn-secondary download-btn">
                <Download size={16} /> Export
              </button>
              <button
                onClick={() => uploadInputRef.current?.click()}
                disabled={uploading}
                className="btn-upload"
              >
                <Upload size={16} />
                {uploading ? 'Uploading…' : 'Upload Excel'}
              </button>
              <button
                onClick={() => {
                  setShowAddProduct(true);
                  setError('');
                }}
                className="btn-primary add-product-btn"
              >
                <Plus /> Add Product
              </button>
            </div>
          </div>

          {/* \u2500\u2500 Upload Report Modal \u2500\u2500 */}
          {showUploadModal && uploadReport && (
            <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
              <div className="modal-content upload-report-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>📥 Upload Report</h3>
                  <button onClick={() => setShowUploadModal(false)} className="modal-close"><X /></button>
                </div>

                {/* Summary badges */}
                <div className="upload-report-summary">
                  <div className="ur-badge ur-badge-added">
                    <CheckCircle size={20} />
                    <div>
                      <div className="ur-num">{uploadReport.added}</div>
                      <div className="ur-lbl">Added</div>
                    </div>
                  </div>
                  <div className="ur-badge ur-badge-dup">
                    <AlertCircle size={20} />
                    <div>
                      <div className="ur-num">{uploadReport.duplicates}</div>
                      <div className="ur-lbl">Duplicates Skipped</div>
                    </div>
                  </div>
                  <div className="ur-badge ur-badge-inv">
                    <X size={20} />
                    <div>
                      <div className="ur-num">{uploadReport.invalid.length}</div>
                      <div className="ur-lbl">Invalid Rows</div>
                    </div>
                  </div>
                  {uploadReport.errors.length > 0 && (
                    <div className="ur-badge ur-badge-err">
                      <AlertCircle size={20} />
                      <div>
                        <div className="ur-num">{uploadReport.errors.length}</div>
                        <div className="ur-lbl">Write Errors</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Invalid row details */}
                {uploadReport.invalid.length > 0 && (
                  <div className="upload-report-section">
                    <h4>⚠️ Invalid Rows</h4>
                    <div className="upload-report-table-wrap">
                      <table className="upload-report-table">
                        <thead><tr><th>Row #</th><th>Reason</th></tr></thead>
                        <tbody>
                          {uploadReport.invalid.map((item, i) => (
                            <tr key={i}>
                              <td style={{whiteSpace:'nowrap', fontWeight:600}}>{item.row}</td>
                              <td style={{fontSize:'0.8rem', color:'#dc2626'}}>{item.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Write errors */}
                {uploadReport.errors.length > 0 && (
                  <div className="upload-report-section">
                    <h4>🔥 Firestore Errors</h4>
                    <ul style={{fontSize:'0.82rem', color:'#dc2626', paddingLeft:'1rem'}}>
                      {uploadReport.errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  </div>
                )}

                {uploadReport.added > 0 && uploadReport.invalid.length === 0 && uploadReport.errors.length === 0 && (
                  <p style={{color:'#059669', fontWeight:600, textAlign:'center', marginTop:'1rem'}}>
                    ✅ All rows imported successfully!
                  </p>
                )}

                <div className="form-actions">
                  <button className="btn-primary" onClick={() => setShowUploadModal(false)}>Done</button>
                </div>
              </div>
            </div>
          )}

          {/* \u2500\u2500 Add Product Modal \u2500\u2500 */}
          {showAddProduct && (
            <div className="modal-overlay">
              <div className="modal-content">
                <div className="modal-header">
                  <h3>Add New Product Specification</h3>
                  <button
                    onClick={() => {
                      setShowAddProduct(false);
                      setError('');
                    }}
                    className="modal-close"
                  >
                    <X />
                  </button>
                </div>
                {renderProductForm(newProduct, setNewProduct, handleAddProduct, false)}
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="filters-section">
            <div className="filters-header">
              <Filter />
              <h3>Filters</h3>
            </div>
            <div className="filters-grid">
              <div className="filter-field">
                <label>Segment</label>
                <select name="segment" value={filters.segment} onChange={handleFilterChange}>
                  <option value="">All</option>
                  {segments.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="filter-field">
                <label>Category</label>
                <select
                  name="category"
                  value={filters.category}
                  onChange={handleFilterChange}
                  disabled={!filters.segment}
                >
                  <option value="">All</option>
                  {filters.segment && categoriesBySegment[filters.segment]?.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="filter-field">
                <label>Product Source</label>
                <select
                  name="productSource"
                   value={filters.productSource}
                   onChange={handleFilterChange}
                   disabled={!filters.category}
                >
                  <option value="">All</option>
                  {filters.category &&
                    Object.keys(productMasterData[filters.segment]?.[filters.category] || {})?.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                </select>
              </div>
              <div className="filter-field">
                <label>Product Name</label>
                <select
                  name="productName"
                  value={filters.productName}
                  onChange={handleFilterChange}
                  disabled={!filters.productSource}
                >
                  <option value="">All</option>
                  {filters.productSource &&
                    Object.keys(productMasterData[filters.segment]?.[filters.category]?.[filters.productSource] || {})?.map(name => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                </select>
              </div>
              <div className="filter-field">
                <label>Finish Type</label>
                <select
                  name="finishType"
                  value={filters.finishType}
                  onChange={handleFilterChange}
                  disabled={!filters.productName}
                >
                  <option value="">All</option>
                  {filters.productName &&
                    Object.keys(
                      productMasterData[filters.segment]?.[filters.category]?.[filters.productSource]?.[
                        filters.productName
                      ]?.finishTypes || {}
                    )?.map(f => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                </select>
              </div>
              <div className="filter-field">
                <label>Specification</label>
                <select
                  name="specification"
                  value={filters.specification}
                  onChange={handleFilterChange}
                  disabled={!filters.finishType}
                >
                  <option value="">All</option>
                  {filters.finishType &&
                    (
                      productMasterData[filters.segment]?.[filters.category]?.[filters.productSource]?.[
                        filters.productName
                      ]?.finishTypes?.[filters.finishType] || []
                    ).map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                </select>
              </div>
            </div>
            <div className="filters-actions">
              <button onClick={clearFilters} className="btn-secondary">
                Clear Filters
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="table-section">
            <div className="table-container">
<table>
                 <thead>
                   <tr>
                     <th>Segment</th>
                     <th>Category</th>
                     <th>Calc.</th>
                     <th>Product Source</th>
                     <th>Product Name</th>
                     <th>Finish Type</th>
                     <th>Specification</th>
                     <th>Wastage % (S/O/J/T)</th>
                     <th>MC % (S/O/J/T)</th>
                     <th>Spec Gram Rate (S/O/J/T)</th>
                     <th>Spec Wastage% (S/O/J/T)</th>
                     <th>Ready Serve SLA</th>
                     <th>Order Serve SLA</th>
                     <th>Actions</th>
                   </tr>
                 </thead>
                 <tbody>
                   {paginatedSpecs.length > 0 ? (
                     paginatedSpecs.map((product) => {
                       const totals = calculateTotals(product);
                       return (
                         <React.Fragment key={product.id}>
                           <tr>
                             <td>{product.segment ?? '—'}</td>
                             <td>{product.category ?? '—'}</td>
                             <td>{(product.buyerDeskCalculation !== '' && product.buyerDeskCalculation != null) ? product.buyerDeskCalculation : '—'}</td>
                             <td>{product.productSource ?? '—'}</td>
                             <td>{product.productName ?? '—'}</td>
                             <td>{product.finishType ?? '—'}</td>
                             <td>{product.specification ?? '—'}</td>
                             <td>
                               {totals.wastage.seller}/{totals.wastage.operator}/{totals.wastage.jmi}/{totals.wastage.total}
                             </td>
                             <td>
                               Set: {totals.setMaking.seller}/{totals.setMaking.operator}/{totals.setMaking.jmi}/{totals.setMaking.total}<br />
                               Gram: {totals.gramMaking.seller}/{totals.gramMaking.operator}/{totals.gramMaking.jmi}/{totals.gramMaking.total}
                             </td>
                             <td>
                               {totals.specificationGramRate.seller}/{totals.specificationGramRate.operator}/{totals.specificationGramRate.jmi}/{totals.specificationGramRate.total}
                             </td>
                             <td>
                               {totals.specificationMaking.seller}/{totals.specificationMaking.operator}/{totals.specificationMaking.jmi}/{totals.specificationMaking.total}
                             </td>
                             <td>{fmtVal(product.maxReadyServeSla)}</td>
                             <td>{fmtVal(product.orderServeSla)}</td>
                             <td>
                               {editingProduct?.id === product.id ? (
                                 <div className="action-buttons">
                                   <button onClick={handleSave} title="Save"><Save /></button>
                                   <button onClick={() => { setEditingProduct(null); setError(''); }} title="Cancel"><X /></button>
                                 </div>
                               ) : (
                                 <div className="action-buttons">
                                   <button onClick={() => handleEdit(product)} title="Edit"><Edit /></button>
                                   <button title="View"><Eye /></button>
                                 </div>
                               )}
                             </td>
                           </tr>
                           {editingProduct?.id === product.id && (
                             <tr>
                               <td colSpan="13">
                                 <div className="edit-form-container">
                                   <h4>Edit Product Details</h4>
                                   {renderProductForm(editingProduct, setEditingProduct, handleSave, true)}
                                 </div>
                               </td>
                             </tr>
                           )}
                         </React.Fragment>
                       );
                     })
                   ) : (
                     <tr>
                       <td colSpan="13" className="no-data">
                         No products match the selected filters.
                       </td>
                     </tr>
                   )}
                 </tbody>
               </table>
             </div>
           </div>
           
           {/* Pagination Controls */}
           {filteredSpecs.length > 0 && (
             <div className="pagination-controls">
               <div className="pagination-info">
                 Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredSpecs.length)} of {filteredSpecs.length} items
               </div>
               <div className="pagination-buttons">
                 <button
                   onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                   disabled={currentPage === 1}
                   className="btn-secondary"
                 >
                   Previous
                 </button>
                 <span className="pagination-page-info">
                   Page {currentPage} of {totalPages}
                 </span>
                 <button
                   onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                   disabled={currentPage === totalPages}
                   className="btn-secondary"
                 >
                   Next
                 </button>
               </div>
               <div className="pagination-options">
                 <label>
                   Items per page:
                   <select
                     value={itemsPerPage}
                     onChange={(e) => {
                       setItemsPerPage(parseInt(e.target.value));
                       setCurrentPage(1); // Reset to first page when changing items per page
                     }}
                   >
                     <option value="5">5</option>
                     <option value="10">10</option>
                     <option value="25">25</option>
                     <option value="50">50</option>
                     <option value="100">100</option>
                   </select>
                 </label>
               </div>
             </div>
           )}
         </div>
       </div>
     </Layout>
   );
 };

export default ProductConstraints;